import React, { ReactNode, Dispatch } from 'react';
import './RetailerSettings.scss';
import {
  Card,
  Button,
  TextInput,
  Dropdown,
  DropdownOption,
  Switch,
  Breadcrumbs,
} from '@orderly/morrisons-component-library';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { IStoreState, IRetailerDetails } from '../../../types';
import {
  updateRetailerSettingsRequest,
  createRetailerRequest,
  setRetailerSettingsData,
  ICreateRetailerRequest,
} from '../../../actions/Admin/adminRetailerSettingsAction';
import { navigateTo } from '../../../routing/Navigation';
import { getCustomerGroupsRequest } from '../../../actions/customerGroupsAction';
import {
  IRetailerSettingsProps,
  IRetailerSettingsState,
} from './IRetailerSettingsProps';
import TimePicker from '../../../components/TimePicker';
import FloatingDropdown from '../../../components/FloatingDropdown';

class RetailerSettingsComponent extends React.Component<
  IRetailerSettingsProps,
  IRetailerSettingsState
> {
  private usingPassedData: boolean = false;

  private hourOptions: { label: string; value: string }[] = Array.from({ length: 24 }, (_, i) => ({
    label: i.toString(),
    value: i.toString(),
  }));

  constructor(props: IRetailerSettingsProps) {
    super(props);

    this.state = {
      retailerName: '',
      productCatalogue: '',
      documentCatalogue: '',
      productCatalogueType: '',

      apiIdentifier: '',
      orderCutoffHour: '',
      orderDailyCount: '',
      orderLeadDays: '',
      orderMessageType: '',
      orderNumberFormat: '',
      ordersView: '',
      orderType: '',
      rebatesEnabled: false,
      printShopFolder: '',

      orderLeadDaysAM: '',
      orderLeadDaysCH: '',
      orderLeadDaysCHNC: '',
      orderLeadDaysCL: '',
      orderLeadDaysCO: '',
      orderLeadDaysCS: '',
      orderLeadDaysFR: '',
      orderLeadDaysTB: '',

      orderCutoffTimeAM: '',
      orderCutoffTimeCH: '',
      orderCutoffTimeCHNC: '',
      orderCutoffTimeCL: '',
      orderCutoffTimeCO: '',
      orderCutoffTimeCS: '',
      orderCutoffTimeFR: '',
      orderCutoffTimeTB: '',

      customerGroup: '',
      combinedOrdering: false,
      viewAllStoresOrders: false,
      dhlClaims: false,
      dhlApiKeyOverride: '',

      subType: '0',
      maxOrderQuantity: '',
      planogramsEnabled: false,
      printshopEnabled: false,
      cashAndCarryEnabled: false,

      npsEnabled: false,
      npsFrequency: 'Never',
      npsTrigger: 'AfterOrder',

      validationErrors: {
        retailerName: '',
        productCatalogue: '',
        documentCatalogue: '',
        productCatalogueType: '',
        apiIdentifier: '',
        orderCutoffHour: '',
        orderLeadDays: '',
        orderDailyCount: '',
        orderMessageType: '',
        orderNumberFormat: '',
        ordersView: '',
        orderType: '',
        customerGroup: '',
        orderLeadDaysAM: '',
        orderLeadDaysCH: '',
        orderLeadDaysCHNC: '',
        orderLeadDaysCL: '',
        orderLeadDaysCO: '',
        orderLeadDaysCS: '',
        orderLeadDaysFR: '',
        orderLeadDaysTB: '',
      },
    };

    this.checkValidation = this.checkValidation.bind(this);
  }

  public componentDidMount(): void {
    const isCreating = this.props.match.params.id === 'create';
    const passedRetailerData = this.props.location &&
      this.props.location.state &&
      this.props.location.state[0] &&
      this.props.location.state[0].retailerData;

    this.props.getCustomerGroups();

    if (!isCreating && passedRetailerData) {
      this.usingPassedData = true;
      this.props.setRetailerSettingsData(passedRetailerData);
      this.populateFormFromMeta(passedRetailerData);
    }
  }

  public componentDidUpdate(prevProps: IRetailerSettingsProps): void {
    if (
      !this.usingPassedData &&
      prevProps.retailerSettings.data !== this.props.retailerSettings.data &&
      this.props.retailerSettings.data
    ) {
      this.populateFormFromMeta(this.props.retailerSettings.data);
    }
  }

  public render(): ReactNode {
    const { retailerSettings } = this.props;
    const isCreating = this.props.match.params.id === 'create';
    const passedRetailerData = this.props.location &&
      this.props.location.state &&
      this.props.location.state[0] &&
      this.props.location.state[0].retailerData;
    const currentRetailer = passedRetailerData || retailerSettings.data;

    const title = isCreating
      ? 'Create New Retailer'
      : 'Retailer Settings';

    if (!isCreating && !currentRetailer) {
      return (
        <div className="retailer-settings-container">
          <Card>
            <h2>Error: Retailer Data Not Found</h2>
            <p>Retailer details must be provided to edit settings.</p>
            <p>Please navigate from the Admin Home page using the Edit button.</p>
            <Button
              type="button"
              className="secondary"
              text="← Back to Admin Home"
              onClick={() => navigateTo('/admin/home')}
            />
          </Card>
        </div>
      );
    }

    return (
      <div className="retailer-settings-container">
        <Breadcrumbs
          className="breadcrumbs"
          onHomeClick={() => navigateTo('/admin/home')}
          onRedirect={() => navigateTo('/admin/home')}
          segments={[
            {
              key: 0,
              text: 'Home',
              url: '/admin/home',
            },
            {
              key: 1,
              text: isCreating ? 'Create Retailer' : 'Retailer Settings',
              url: null,
            },
          ]}
        />

        <div className="retailer-settings-header">
          <div className="header-content">
            <h1>{title}</h1>
            <div className="header-actions">
              <Button
                type="button"
                className="secondary"
                text="Reset"
                onClick={this.handleReset}
              />
              <Button
                type="button"
                className="primary"
                text={isCreating ? 'Create' : 'Save'}
                onClick={this.handleSave}
                disabled={
                  !this.checkValidation() ||
                  (isCreating
                    ? this.props.createRetailer.loading
                    : this.props.updateRetailerMeta.loading)
                }
              />
              <Button
                type="button"
                className="secondary"
                text="Export JSON"
                onClick={this.handleExportJson}
              />
            </div>
          </div>
        </div>

        <form className="retailer-settings-form" noValidate>
          <Card className="retailer-section">
            <h3>Retailer Information</h3>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="Retailer Name *"
                  name="retailerName"
                  type="text"
                  value={this.state.retailerName}
                  onChange={this.handleTextInputChange('retailerName')}
                  error={this.state.validationErrors.retailerName}
                />
              </div>
              <div className="form-group">
                <TextInput
                  label="Product Catalogue *"
                  name="productCatalogue"
                  type="text"
                  value={this.state.productCatalogue}
                  onChange={this.handleTextInputChange('productCatalogue')}
                  error={this.state.validationErrors.productCatalogue}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="Product Catalogue Type *"
                  name="productCatalogueType"
                  type="text"
                  value={this.state.productCatalogueType}
                  onChange={this.handleTextInputChange('productCatalogueType')}
                  error={this.state.validationErrors.productCatalogueType}
                />
              </div>
              <div className="form-group">
                <TextInput
                  label="Document Catalogue *"
                  name="documentCatalogue"
                  type="text"
                  value={this.state.documentCatalogue}
                  onChange={this.handleTextInputChange('documentCatalogue')}
                  error={this.state.validationErrors.documentCatalogue}
                />
              </div>
            </div>
          </Card>

          <Card className="retailer-section">
            <h3>Order Settings</h3>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="API Identifier *"
                  name="apiIdentifier"
                  type="text"
                  value={this.state.apiIdentifier}
                  onChange={this.handleTextInputChange('apiIdentifier')}
                  error={this.state.validationErrors.apiIdentifier}
                />
              </div>
              <div className="form-group">
                <FloatingDropdown
                  label="Order Cutoff Hour *"
                  name="orderCutoffHour"
                  value={this.state.orderCutoffHour}
                  onChange={option => this.handleDropdownChange('orderCutoffHour', option)}
                  options={this.hourOptions}
                  error={this.state.validationErrors.orderCutoffHour}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="Order Daily Count"
                  name="orderDailyCount"
                  type="number"
                  value={this.state.orderDailyCount}
                  onChange={this.handleNumericInputChange('orderDailyCount')}
                  error={this.state.validationErrors.orderDailyCount}
                />
              </div>
              <div className="form-group">
                <TextInput
                  label="Default Order Lead Days *"
                  name="orderLeadDays"
                  type="number"
                  value={this.state.orderLeadDays}
                  onChange={this.handleOrderLeadDaysChange('orderLeadDays')}
                  error={this.state.validationErrors.orderLeadDays}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="Max Order Quantity"
                  name="maxOrderQuantity"
                  type="number"
                  value={this.state.maxOrderQuantity}
                  onChange={this.handleNumericInputChange('maxOrderQuantity')}
                  error=""
                />
              </div>
              <div className="form-group" />
            </div>

            <div className="category-settings-section">
              <h4>Category Settings</h4>
              <div className="category-grid">
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days CL"
                      name="orderLeadDaysCL"
                      type="number"
                      value={this.state.orderLeadDaysCL}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysCL')}
                      error={this.state.validationErrors.orderLeadDaysCL}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time CL"
                      name="orderCutoffTimeCL"
                      value={this.state.orderCutoffTimeCL}
                      onChange={this.handleTextInputChange('orderCutoffTimeCL')}
                      onClear={() => this.handleClearTime('orderCutoffTimeCL')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days CO"
                      name="orderLeadDaysCO"
                      type="number"
                      value={this.state.orderLeadDaysCO}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysCO')}
                      error={this.state.validationErrors.orderLeadDaysCO}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time CO"
                      name="orderCutoffTimeCO"
                      value={this.state.orderCutoffTimeCO}
                      onChange={this.handleTextInputChange('orderCutoffTimeCO')}
                      onClear={() => this.handleClearTime('orderCutoffTimeCO')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days CS"
                      name="orderLeadDaysCS"
                      type="number"
                      value={this.state.orderLeadDaysCS}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysCS')}
                      error={this.state.validationErrors.orderLeadDaysCS}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time CS"
                      name="orderCutoffTimeCS"
                      value={this.state.orderCutoffTimeCS}
                      onChange={this.handleTextInputChange('orderCutoffTimeCS')}
                      onClear={() => this.handleClearTime('orderCutoffTimeCS')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days FR"
                      name="orderLeadDaysFR"
                      type="number"
                      value={this.state.orderLeadDaysFR}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysFR')}
                      error={this.state.validationErrors.orderLeadDaysFR}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time FR"
                      name="orderCutoffTimeFR"
                      value={this.state.orderCutoffTimeFR}
                      onChange={this.handleTextInputChange('orderCutoffTimeFR')}
                      onClear={() => this.handleClearTime('orderCutoffTimeFR')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days TB"
                      name="orderLeadDaysTB"
                      type="number"
                      value={this.state.orderLeadDaysTB}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysTB')}
                      error={this.state.validationErrors.orderLeadDaysTB}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time TB"
                      name="orderCutoffTimeTB"
                      value={this.state.orderCutoffTimeTB}
                      onChange={this.handleTextInputChange('orderCutoffTimeTB')}
                      onClear={() => this.handleClearTime('orderCutoffTimeTB')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days AM"
                      name="orderLeadDaysAM"
                      type="number"
                      value={this.state.orderLeadDaysAM}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysAM')}
                      error={this.state.validationErrors.orderLeadDaysAM}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time AM"
                      name="orderCutoffTimeAM"
                      value={this.state.orderCutoffTimeAM}
                      onChange={this.handleTextInputChange('orderCutoffTimeAM')}
                      onClear={() => this.handleClearTime('orderCutoffTimeAM')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days CH"
                      name="orderLeadDaysCH"
                      type="number"
                      value={this.state.orderLeadDaysCH}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysCH')}
                      error={this.state.validationErrors.orderLeadDaysCH}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time CH"
                      name="orderCutoffTimeCH"
                      value={this.state.orderCutoffTimeCH}
                      onChange={this.handleTextInputChange('orderCutoffTimeCH')}
                      onClear={() => this.handleClearTime('orderCutoffTimeCH')}
                    />
                  </div>
                </div>
                <div className="category-row">
                  <div className="form-group">
                    <TextInput
                      label="Order Lead Days CHNC"
                      name="orderLeadDaysCHNC"
                      type="number"
                      value={this.state.orderLeadDaysCHNC}
                      onChange={this.handleOrderLeadDaysChange('orderLeadDaysCHNC')}
                      error={this.state.validationErrors.orderLeadDaysCHNC}
                    />
                  </div>
                  <div className="form-group">
                    <TimePicker
                      label="Order Cutoff time CHNC"
                      name="orderCutoffTimeCHNC"
                      value={this.state.orderCutoffTimeCHNC}
                      onChange={this.handleTextInputChange('orderCutoffTimeCHNC')}
                      onClear={() => this.handleClearTime('orderCutoffTimeCHNC')}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <FloatingDropdown
                  label="Order Message Type *"
                  name="orderMessageType"
                  value={this.state.orderMessageType}
                  onChange={option => this.handleDropdownChange('orderMessageType', option)}
                  options={[
                    { label: 'Convert', value: 'CNV' },
                    { label: 'Daily', value: 'DAILY' },
                  ]}
                  error={this.state.validationErrors.orderMessageType}
                />
              </div>
              <div className="form-group">
                <TextInput
                  label="Order Number Format *"
                  name="orderNumberFormat"
                  type="text"
                  value={this.state.orderNumberFormat}
                  onChange={this.handleTextInputChange('orderNumberFormat')}
                  error={this.state.validationErrors.orderNumberFormat}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <FloatingDropdown
                  label="Orders View *"
                  name="ordersView"
                  value={this.state.ordersView}
                  onChange={option => this.handleDropdownChange('ordersView', option)}
                  options={[
                    { label: 'List View', value: 'list' },
                    { label: 'Search View', value: 'search' },
                  ]}
                  error={this.state.validationErrors.ordersView}
                />
              </div>
              <div className="form-group">
                <FloatingDropdown
                  label="Order Type *"
                  name="orderType"
                  value={this.state.orderType}
                  onChange={option => this.handleDropdownChange('orderType', option)}
                  options={[
                    { label: 'Independent', value: '1' },
                    { label: 'Bulk', value: '2' },
                  ]}
                  error={this.state.validationErrors.orderType}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="Rebates Enabled"
                    name="rebatesEnabled"
                    checked={this.state.rebatesEnabled}
                    onChange={(name, checked) => this.handleSwitchChange('rebatesEnabled', checked)}
                  />
                </div>
              </div>
              <div className="form-group">
                <FloatingDropdown
                  label="Print Shop Folder"
                  name="subType"
                  value={this.state.subType}
                  onChange={option => this.handleDropdownChange('subType', option)}
                  options={[
                    { label: 'None', value: '0' },
                    { label: 'Motor Fuel Group', value: '1' },
                    { label: 'Morrisons Daily', value: '2' },
                    { label: 'Morrisons CoBranded', value: '3' },
                  ]}
                />
              </div>
            </div>
          </Card>

          <Card className="retailer-section">
            <h3>Features</h3>
            <div className="form-row">
              <div className="form-group">
                <FloatingDropdown
                  label="Customer Group *"
                  name="customerGroup"
                  value={this.state.customerGroup}
                  onChange={option => this.handleDropdownChange('customerGroup', option)}
                  options={this.getCustomerGroupOptions()}
                  error={this.state.validationErrors.customerGroup}
                />
              </div>
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="Combined Ordering"
                    name="combinedOrdering"
                    checked={this.state.combinedOrdering}
                    onChange={(name, checked) => this.handleSwitchChange('combinedOrdering', checked)}

                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="View All Stores Orders"
                    name="viewAllStoresOrders"
                    checked={this.state.viewAllStoresOrders}
                    onChange={(name, checked) => this.handleSwitchChange('viewAllStoresOrders', checked)}

                  />
                </div>
              </div>
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="DHL Claims"
                    name="dhlClaims"
                    checked={this.state.dhlClaims}
                    onChange={(name, checked) => this.handleSwitchChange('dhlClaims', checked)}

                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="Planograms Enabled"
                    name="planogramsEnabled"
                    checked={this.state.planogramsEnabled}
                    onChange={(name, checked) => this.handleSwitchChange('planogramsEnabled', checked)}
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="Printshop Enabled"
                    name="printshopEnabled"
                    checked={this.state.printshopEnabled}
                    onChange={(name, checked) => this.handleSwitchChange('printshopEnabled', checked)}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="Cash And Carry Enabled"
                    name="cashAndCarryEnabled"
                    checked={this.state.cashAndCarryEnabled}
                    onChange={(name, checked) => this.handleSwitchChange('cashAndCarryEnabled', checked)}
                  />
                </div>
              </div>
              <div className="form-group" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <TextInput
                  label="DHL API Key Override"
                  name="dhlApiKeyOverride"
                  type="text"
                  value={this.state.dhlApiKeyOverride}
                  onChange={this.handleTextInputChange('dhlApiKeyOverride')}
                  error=""

                />
              </div>
              <div className="form-group" />
            </div>
          </Card>

          <Card className="retailer-section">
            <h3>NPS Settings</h3>
            <div className="form-row">
              <div className="form-group">
                <div className="switch-group">
                  <Switch
                    label="NPS Enabled"
                    name="npsEnabled"
                    checked={this.state.npsEnabled}
                    onChange={(name, checked) => this.handleSwitchChange('npsEnabled', checked)}
                  />
                </div>
              </div>
              <div className="form-group" />
            </div>
            {this.state.npsEnabled && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <FloatingDropdown
                      label="NPS Frequency"
                      name="npsFrequency"
                      value={this.state.npsFrequency}
                      onChange={option => this.handleDropdownChange('npsFrequency', option)}
                      options={[
                        { label: 'Never', value: 'Never' },
                        { label: 'Weekly', value: 'Weekly' },
                        { label: 'Monthly', value: 'Monthly' },
                      ]}
                      error=""
                    />
                  </div>
                  <div className="form-group">
                    <FloatingDropdown
                      label="NPS Trigger Point"
                      name="npsTrigger"
                      value={this.state.npsTrigger}
                      onChange={option => this.handleDropdownChange('npsTrigger', option)}
                      options={[
                        { label: 'Before Order', value: 'BeforeOrder' },
                        { label: 'After Order', value: 'AfterOrder' },
                      ]}
                      error=""
                    />
                  </div>
                </div>
              </>
            )}
          </Card>
        </form>
      </div>
    );
  }

  private parseBooleanValue(value: any): boolean {
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      return lowerValue === 'true' || lowerValue === '1';
    }
    return Boolean(value);
  }

  private parseOrderCutoffHour(value: string | undefined): string {
    if (!value) return '';
    const num = parseInt(value, 10);
    return isNaN(num) ? '' : num.toString();
  }

  private handleClearTime = (fieldName: string): void => {
    this.setState(prevState => ({
      ...prevState,
      [fieldName]: '',
      validationErrors: {
        ...prevState.validationErrors,
        [fieldName]: '',
      },
    } as IRetailerSettingsState));
  }

  private populateFormFromMeta(retailer: IRetailerDetails): void {
    const meta = retailer.meta || {};
    this.setState({
      retailerName: retailer.externalIdentifier || '',
      productCatalogue: meta.ProductCatalogue || '',
      documentCatalogue: meta.DocumentCatalogue || '',
      productCatalogueType: meta.ProductCatalogueType || '',

      apiIdentifier: meta.ApiIdentifier || '',
      orderCutoffHour: this.parseOrderCutoffHour(meta.OrderCutoffHour),
      orderDailyCount: meta.OrderDailyCount || '',
      orderLeadDays: meta.OrderLeadDays || '',
      orderMessageType: meta.OrderMessageType || '',
      orderNumberFormat: meta.OrderNumberFormat || '',
      ordersView: meta.OrdersView || '',
      orderType: meta.OrderType || '',
      rebatesEnabled: this.parseBooleanValue(meta.RebatesEnabled),
      printShopFolder: meta.PrintShopFolder || '',

      orderLeadDaysAM: meta.OrderLeadDaysAM || '',
      orderLeadDaysCH: meta.OrderLeadDaysCH || '',
      orderLeadDaysCHNC: meta.OrderLeadDaysCHNC || '',
      orderLeadDaysCL: meta.OrderLeadDaysCL || '',
      orderLeadDaysCO: meta.OrderLeadDaysCO || '',
      orderLeadDaysCS: meta.OrderLeadDaysCS || '',
      orderLeadDaysFR: meta.OrderLeadDaysFR || '',
      orderLeadDaysTB: meta.OrderLeadDaysTB || '',

      orderCutoffTimeAM: meta.OrderCutoffTimeAM || '',
      orderCutoffTimeCH: meta.OrderCutoffTimeCH || '',
      orderCutoffTimeCHNC: meta.OrderCutoffTimeCHNC || '',
      orderCutoffTimeCL: meta.OrderCutoffTimeCL || '',
      orderCutoffTimeCO: meta.OrderCutoffTimeCO || '',
      orderCutoffTimeCS: meta.OrderCutoffTimeCS || '',
      orderCutoffTimeFR: meta.OrderCutoffTimeFR || '',
      orderCutoffTimeTB: meta.OrderCutoffTimeTB || '',

      customerGroup: meta.CustomerGroup || '',
      combinedOrdering: this.parseBooleanValue(meta.CombinedOrdering),
      viewAllStoresOrders: this.parseBooleanValue(meta.ViewAllStoresOrders),
      dhlClaims: this.parseBooleanValue(meta.DhlClaimsEnabled),
      dhlApiKeyOverride: meta.DhlApiKeyOverride || '',

      subType: meta.SubType || '',
      maxOrderQuantity: meta.MaxOrderQuantity || '',
      planogramsEnabled: this.parseBooleanValue(meta.PlanogramsEnabled),
      printshopEnabled: this.parseBooleanValue(meta.PrintshopEnabled),
      cashAndCarryEnabled: this.parseBooleanValue(meta.CashAndCarryEnabled),

      npsEnabled: this.parseBooleanValue(meta.NpsEnabled),
      npsFrequency: meta.NpsFrequency || 'Never',
      npsTrigger: meta.NpsTrigger || 'AfterOrder',
    });
  }

  private handleTextInputChange = (name: string) => (value: string): void => {
    this.setState(prevState => ({
      ...prevState,
      [name]: value,
      validationErrors: {
        ...prevState.validationErrors,
        [name]: '',
      },
    } as IRetailerSettingsState));
  }

  private handleNumericInputChange = (name: string) => (value: string): void => {
    const numericValue = value.replace(/[^0-9]/g, '');
    this.setState(prevState => ({
      ...prevState,
      [name]: numericValue,
      validationErrors: {
        ...prevState.validationErrors,
        [name]: '',
      },
    } as IRetailerSettingsState));
  }

  private handleOrderLeadDaysChange = (name: string) => (value: string): void => {
    const numericValue = value.replace(/[^0-9]/g, '');
    let errorMessage = '';
    if (numericValue !== '' && parseInt(numericValue, 10) < 1) {
      errorMessage = 'Order lead days must be at least 1';
    }

    this.setState(prevState => ({
      ...prevState,
      [name]: numericValue,
      validationErrors: {
        ...prevState.validationErrors,
        [name]: errorMessage,
      },
    } as IRetailerSettingsState));
  }

  private handleCheckboxChange = (name: string, checked: boolean): void => {
    this.setState(prevState => ({
      ...prevState,
      [name]: checked,
    } as IRetailerSettingsState));
  }

  private handleSwitchChange = (name: string, checked: boolean): void => {
    this.setState(prevState => ({
      ...prevState,
      [name]: checked,
    } as IRetailerSettingsState));
  }

  private handleDropdownChange = (name: string, option: { label: string; value: string | number }): void => {
    this.setState(prevState => ({
      ...prevState,
      [name]: option.value,
      validationErrors: {
        ...prevState.validationErrors,
        [name]: '',
      },
    } as IRetailerSettingsState));
  }

  private handleSave = (): void => {
    const validationResult = this.validateRequiredFields();
    if (!validationResult.isValid) {
      this.setState(prevState => ({
        ...prevState,
        validationErrors: validationResult.fieldErrors,
      } as IRetailerSettingsState));
      toastr.error('Validation Error', 'Please fill in all required fields');
      return;
    }

    if (this.state.dhlClaims && !this.state.dhlApiKeyOverride.trim()) {
      const confirmMessage = 'DHL Claims is enabled but no DHL API Key Override is provided. ' +
        'This may cause issues with DHL claims functionality. Do you want to continue?';
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    const isCreating = this.props.match.params.id === 'create';

    if (isCreating) {
      this.handleCreateRetailer();
    } else {
      this.handleUpdateRetailer();
    }
  }

  private checkValidation(): boolean {
    const validationResult = this.validateRequiredFields();
    return validationResult.isValid;
  }

  private validateRequiredFields(): { isValid: boolean; fieldErrors: { [key: string]: string } } {
    const fieldErrors: { [key: string]: string } = {
      retailerName: '',
      productCatalogue: '',
      documentCatalogue: '',
      productCatalogueType: '',
      apiIdentifier: '',
      orderCutoffHour: '',
      orderLeadDays: '',
      orderDailyCount: '',
      orderMessageType: '',
      orderNumberFormat: '',
      ordersView: '',
      orderType: '',
      customerGroup: '',
      orderLeadDaysAM: '',
      orderLeadDaysCH: '',
      orderLeadDaysCHNC: '',
      orderLeadDaysCL: '',
      orderLeadDaysCO: '',
      orderLeadDaysCS: '',
      orderLeadDaysFR: '',
      orderLeadDaysTB: '',
    };

    if (!this.state.retailerName.trim()) {
      fieldErrors.retailerName = 'Retailer Name is required';
    }
    if (!this.state.productCatalogue.trim()) {
      fieldErrors.productCatalogue = 'Product Catalogue is required';
    }
    if (!this.state.documentCatalogue.trim()) {
      fieldErrors.documentCatalogue = 'Document Catalogue is required';
    }
    if (!this.state.productCatalogueType.trim()) {
      fieldErrors.productCatalogueType = 'Product Catalogue Type is required';
    }
    if (!this.state.apiIdentifier.trim()) {
      fieldErrors.apiIdentifier = 'API Identifier is required';
    }
    if (!this.state.orderCutoffHour.trim()) {
      fieldErrors.orderCutoffHour = 'Order Cutoff Hour is required';
    }
    if (!this.state.orderLeadDays.trim()) {
      fieldErrors.orderLeadDays = 'Default Order Lead Days is required';
    } else if (parseInt(this.state.orderLeadDays, 10) < 1) {
      fieldErrors.orderLeadDays = 'Order lead days must be at least 1';
    }
    if (!this.state.orderMessageType.trim()) {
      fieldErrors.orderMessageType = 'Order Message Type is required';
    }
    if (!this.state.orderNumberFormat.trim()) {
      fieldErrors.orderNumberFormat = 'Order Number Format is required';
    }
    if (!this.state.ordersView.trim()) {
      fieldErrors.ordersView = 'Orders View is required';
    }
    if (!this.state.orderType.trim()) {
      fieldErrors.orderType = 'Order Type is required';
    }
    if (!this.state.customerGroup.trim()) {
      fieldErrors.customerGroup = 'Customer Group is required';
    }

    // Validate category-specific order lead days (minimum value of 1 if provided)
    const categoryLeadDaysFields = [
      'orderLeadDaysAM', 'orderLeadDaysCH', 'orderLeadDaysCHNC', 'orderLeadDaysCL',
      'orderLeadDaysCO', 'orderLeadDaysCS', 'orderLeadDaysFR', 'orderLeadDaysTB',
    ];

    categoryLeadDaysFields.forEach((fieldName) => {
      const value = (this.state as any)[fieldName];
      if (value && value.trim() !== '' && parseInt(value, 10) < 1) {
        fieldErrors[fieldName] = 'Order lead days must be at least 1';
      }
    });

    const hasErrors = Object.values(fieldErrors).some(error => error !== '');

    return {
      fieldErrors,
      isValid: !hasErrors,
    };
  }

  private handleCreateRetailer(): void {
    const meta: { [key: string]: string } = {};

    Object.keys(this.state).forEach((key) => {
      if (
        key === 'retailerName' ||
        key === 'productCatalogue' ||
        key === 'documentCatalogue' ||
        key === 'productCatalogueType'
      ) {
        return;
      }

      const value = (this.state as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        const metaKeyMapping: { [key: string]: string } = {
          apiIdentifier: 'ApiIdentifier',
          orderCutoffHour: 'OrderCutoffHour',
          orderDailyCount: 'OrderDailyCount',
          orderLeadDays: 'OrderLeadDays',
          orderMessageType: 'OrderMessageType',
          orderNumberFormat: 'OrderNumberFormat',
          ordersView: 'OrdersView',
          orderType: 'OrderType',
          rebatesEnabled: 'RebatesEnabled',
          printShopFolder: 'PrintShopFolder',
          orderLeadDaysAM: 'OrderLeadDaysAM',
          orderLeadDaysCH: 'OrderLeadDaysCH',
          orderLeadDaysCHNC: 'OrderLeadDaysCHNC',
          orderLeadDaysCL: 'OrderLeadDaysCL',
          orderLeadDaysCO: 'OrderLeadDaysCO',
          orderLeadDaysCS: 'OrderLeadDaysCS',
          orderLeadDaysFR: 'OrderLeadDaysFR',
          orderLeadDaysTB: 'OrderLeadDaysTB',
          orderCutoffTimeAM: 'OrderCutoffTimeAM',
          orderCutoffTimeCH: 'OrderCutoffTimeCH',
          orderCutoffTimeCHNC: 'OrderCutoffTimeCHNC',
          orderCutoffTimeCL: 'OrderCutoffTimeCL',
          orderCutoffTimeCO: 'OrderCutoffTimeCO',
          orderCutoffTimeCS: 'OrderCutoffTimeCS',
          orderCutoffTimeFR: 'OrderCutoffTimeFR',
          orderCutoffTimeTB: 'OrderCutoffTimeTB',
          customerGroup: 'CustomerGroup',
          combinedOrdering: 'CombinedOrdering',
          viewAllStoresOrders: 'ViewAllStoresOrders',
          dhlClaims: 'DhlClaimsEnabled',
          dhlApiKeyOverride: 'DhlApiKeyOverride',
          subType: 'SubType',
          maxOrderQuantity: 'MaxOrderQuantity',
          planogramsEnabled: 'PlanogramsEnabled',
          printshopEnabled: 'PrintshopEnabled',
          cashAndCarryEnabled: 'CashAndCarryEnabled',
          npsEnabled: 'NpsEnabled',
          npsFrequency: 'NpsFrequency',
          npsTrigger: 'NpsTrigger',
        };

        const metaKey = metaKeyMapping[key] || key;

        if (typeof value === 'boolean') {
          meta[metaKey] = value.toString();
        } else if (typeof value === 'string' && value.trim() !== '') {
          meta[metaKey] = value.trim();
        } else if (typeof value === 'number') {
          meta[metaKey] = value.toString();
        }
      }
    });

    if (!meta['MaxOrderQuantity'] || meta['MaxOrderQuantity'].trim() === '') {
      meta['MaxOrderQuantity'] = '999999';
    }

    const createData: ICreateRetailerRequest = {
      RetailerName: this.state.retailerName.trim(),
      ProductCatalogue: this.state.productCatalogue.trim(),
      DocumentCatalogue: this.state.documentCatalogue.trim(),
      ProductCatalogueType: this.state.productCatalogueType.trim(),
      Meta: meta,
    };

    this.props.createRetailerRequest(
      createData,
      () => {
        toastr.success('Success', 'Retailer created successfully');
        navigateTo('/admin/home');
      },
      () => {
        toastr.error('Error', 'Failed to create retailer');
        console.error('Failed to create retailer');
      },
    );
  }

  private handleUpdateRetailer(): void {
    const passedRetailerData = this.props.location &&
      this.props.location.state &&
      this.props.location.state[0] &&
      this.props.location.state[0].retailerData;
    const currentRetailer = passedRetailerData || this.props.retailerSettings.data;

    if (!currentRetailer) {
      return;
    }

    const updatedMeta: { [key: string]: string } = {};

    const stateToMetaMapping: { [key: string]: string } = {
      apiIdentifier: 'ApiIdentifier',
      orderCutoffHour: 'OrderCutoffHour',
      orderDailyCount: 'OrderDailyCount',
      orderLeadDays: 'OrderLeadDays',
      orderMessageType: 'OrderMessageType',
      orderNumberFormat: 'OrderNumberFormat',
      ordersView: 'OrdersView',
      orderType: 'OrderType',

      orderLeadDaysAM: 'OrderLeadDaysAM',
      orderLeadDaysCH: 'OrderLeadDaysCH',
      orderLeadDaysCHNC: 'OrderLeadDaysCHNC',
      orderLeadDaysCL: 'OrderLeadDaysCL',
      orderLeadDaysCO: 'OrderLeadDaysCO',
      orderLeadDaysCS: 'OrderLeadDaysCS',
      orderLeadDaysFR: 'OrderLeadDaysFR',
      orderLeadDaysTB: 'OrderLeadDaysTB',

      orderCutoffTimeAM: 'OrderCutoffTimeAM',
      orderCutoffTimeCH: 'OrderCutoffTimeCH',
      orderCutoffTimeCHNC: 'OrderCutoffTimeCHNC',
      orderCutoffTimeCL: 'OrderCutoffTimeCL',
      orderCutoffTimeCO: 'OrderCutoffTimeCO',
      orderCutoffTimeCS: 'OrderCutoffTimeCS',
      orderCutoffTimeFR: 'OrderCutoffTimeFR',
      orderCutoffTimeTB: 'OrderCutoffTimeTB',

      customerGroup: 'CustomerGroup',
      dhlApiKeyOverride: 'DhlApiKeyOverride',
      subType: 'SubType',
      maxOrderQuantity: 'MaxOrderQuantity',

      rebatesEnabled: 'RebatesEnabled',
      combinedOrdering: 'CombinedOrdering',
      viewAllStoresOrders: 'ViewAllStoresOrders',
      dhlClaims: 'DhlClaimsEnabled',
      planogramsEnabled: 'PlanogramsEnabled',
      printshopEnabled: 'PrintshopEnabled',
      cashAndCarryEnabled: 'CashAndCarryEnabled',
      npsEnabled: 'NpsEnabled',
      npsFrequency: 'NpsFrequency',
      npsTrigger: 'NpsTrigger',
    };

    Object.keys(stateToMetaMapping).forEach((stateKey) => {
      const metaKey = stateToMetaMapping[stateKey];
      const value = (this.state as any)[stateKey];

      if (value !== undefined && value !== null) {
        if (typeof value === 'boolean') {
          updatedMeta[metaKey] = value ? 'true' : 'false';
        } else if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (trimmedValue !== '') {
            updatedMeta[metaKey] = trimmedValue;
          }
        } else if (typeof value === 'number') {
          updatedMeta[metaKey] = value.toString();
        }
      }
    });

    if (this.state.productCatalogue) {
      updatedMeta['ProductCatalogue'] = this.state.productCatalogue.trim();
    }
    if (this.state.productCatalogueType) {
      updatedMeta['ProductCatalogueType'] = this.state.productCatalogueType.trim();
    }
    if (this.state.documentCatalogue) {
      updatedMeta['DocumentCatalogue'] = this.state.documentCatalogue.trim();
    }

    if (!updatedMeta['MaxOrderQuantity'] || updatedMeta['MaxOrderQuantity'].trim() === '') {
      updatedMeta['MaxOrderQuantity'] = '999999';
    }

    const updatedRetailer: IRetailerDetails = {
      id: currentRetailer.id,
      externalIdentifier: this.state.retailerName.trim() || currentRetailer.externalIdentifier,
      meta: updatedMeta,
      ...(currentRetailer.stores && { stores: currentRetailer.stores }),
    };

    this.props.updateRetailerSettings(
      updatedRetailer,
      () => {
        this.props.setRetailerSettingsData(updatedRetailer);
        toastr.success('Success', 'Retailer settings updated successfully');
        console.log('Retailer settings updated successfully');
        navigateTo('/admin/home');
      },
      () => {
        toastr.error('Error', 'Failed to update retailer settings');
        console.error('Failed to update retailer settings');
      },
    );
  }

  private handleReset = (): void => {
    const isCreating = this.props.match.params.id === 'create';
    if (isCreating) {
      this.setState({
        retailerName: '',
        productCatalogue: '',
        documentCatalogue: '',
        productCatalogueType: '',
        apiIdentifier: '',
        orderCutoffHour: '',
        orderDailyCount: '',
        orderLeadDays: '',
        orderMessageType: '',
        orderNumberFormat: '',
        ordersView: '',
        orderType: '',
        rebatesEnabled: false,
        printShopFolder: '',
        orderLeadDaysAM: '',
        orderLeadDaysCH: '',
        orderLeadDaysCHNC: '',
        orderLeadDaysCL: '',
        orderLeadDaysCO: '',
        orderLeadDaysCS: '',
        orderLeadDaysFR: '',
        orderLeadDaysTB: '',
        orderCutoffTimeAM: '',
        orderCutoffTimeCH: '',
        orderCutoffTimeCHNC: '',
        orderCutoffTimeCL: '',
        orderCutoffTimeCO: '',
        orderCutoffTimeCS: '',
        orderCutoffTimeFR: '',
        orderCutoffTimeTB: '',
        customerGroup: '',
        combinedOrdering: false,
        viewAllStoresOrders: false,
        dhlClaims: false,
        dhlApiKeyOverride: '',
        subType: '0',
        maxOrderQuantity: '',
        planogramsEnabled: false,
        printshopEnabled: false,
        cashAndCarryEnabled: false,
        npsEnabled: false,
        npsFrequency: 'Never',
        npsTrigger: 'AfterOrder',
        validationErrors: {
          retailerName: '',
          productCatalogue: '',
          documentCatalogue: '',
          productCatalogueType: '',
          apiIdentifier: '',
          orderCutoffHour: '',
          orderLeadDays: '',
          orderDailyCount: '',
          orderMessageType: '',
          orderNumberFormat: '',
          ordersView: '',
          orderType: '',
          customerGroup: '',
          orderLeadDaysAM: '',
          orderLeadDaysCH: '',
          orderLeadDaysCHNC: '',
          orderLeadDaysCL: '',
          orderLeadDaysCO: '',
          orderLeadDaysCS: '',
          orderLeadDaysFR: '',
          orderLeadDaysTB: '',
        },
      });
    } else {
      const passedRetailerData = this.props.location &&
        this.props.location.state &&
        this.props.location.state[0] &&
        this.props.location.state[0].retailerData;
      const currentRetailer = passedRetailerData || this.props.retailerSettings.data;
      if (currentRetailer) {
        this.populateFormFromMeta(currentRetailer);
      }
    }
  }

  private getCustomerGroupOptions = (): { label: string; value: string | number }[] => {
    if (this.props.customerGroups.data && this.props.customerGroups.data.length > 0) {
      return this.props.customerGroups.data
        .filter(group => group && group.trim() !== '' && group !== ')')
        .map(group => ({
          label: group.charAt(0).toUpperCase() + group.slice(1).toLowerCase().replace('-', ' '),
          value: group,
        }));
    }
    return [];
  }

  private handleExportJson = (): void => {
    const exportData = {
      retailerSettings: this.state,
      timestamp: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

    const exportFileDefaultName = `retailer-settings-${this.state.retailerName || 'new'}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }
}

const mapStateToProps = (state: IStoreState) => {
  return {
    retailerSettings: state.adminRetailerSettings,
    updateRetailerMeta: state.updateRetailerMeta,
    createRetailer: state.createRetailer,
    customerGroups: state.customerGroups,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    setRetailerSettingsData: (retailerData: IRetailerDetails) => dispatch(setRetailerSettingsData(retailerData)),
    updateRetailerSettings: (retailer: IRetailerDetails, onSuccess: () => void, onFailure: () => void) =>
      dispatch(updateRetailerSettingsRequest(retailer, onSuccess, onFailure)),
    createRetailerRequest: (retailerData: ICreateRetailerRequest, onSuccess: () => void, onFailure: () => void) =>
      dispatch(createRetailerRequest(retailerData, onSuccess, onFailure)),
    getCustomerGroups: () => dispatch(getCustomerGroupsRequest()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RetailerSettingsComponent);
